// @flow

import * as React from 'react';
import { every, get, sum, without } from 'lodash/fp';
import round from 'lodash/round';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { padding } from 'polished';
import pluralize from 'pluralize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator } from '@fortawesome/free-solid-svg-icons';

import playerSvg from 'app/images/ui/icon-player.svg';
import horseSvg from 'app/images/ui/icon-sprites/icon-sport-horse.svg';
import lineUpSelector from 'app/selectors/lineUpSelector';
import * as challengeActions from 'app/actions/challengeActions';
import { FAVORITE, UNSUBMITTED } from 'app/constants/lineupTypeFilters';
import t from 'app/utils/translate';
import { getCostSplit } from 'app/utils/priceUtils';
import Responsive from 'app/components/responsive/Responsive';
import RadioTick from 'app/components/commons/RadioTick';
import { hover } from 'app/utils/cssUtils';
import RowButton from 'app/components/commons/RowButton';
import Tappable from 'app/components/commons/Tappable';
import RadioButton from 'app/components/commons/RadioButton';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';
import Title from 'app/components/commons/Title';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import OpenChallengeModal from 'app/components/challenge/OpenChallengeModal';
import * as myTeamsActions from '../../actions/myTeamsActions';
import loadableLineupsSelector from '../../selectors/loadableLineupsSelector';
import selectedLineupIdsSelector from '../../selectors/selectedLineupIdsSelector';
import Button from '../commons/Button';
import Icon from '../commons/Icon';
import Loader from '../commons/Loader';
import Modal from '../commons/Modal';
import Rows from '../layout/Rows';
import LineUpSection from './LineUpSection';
import LineUpTable from './LineUpTable';
import SalaryUtilisation from './SalaryUtilisation';
import AutofillButton from './AutofillButton';
import SelectedEntityRow from './SelectedEntityRow';
import LineUpHeader, { LineUpHeaderWrapper, SportsIcon } from './LineUpHeader';
import { getFactoredEntityPositionSalary } from '../../selectors/salaryExpenditureSelector';

type Props = {
  allLocked: boolean,
  // balance?: number,
  bonusBalance?: number,
  enableSmartLineUp?: boolean,
  // bonusFreeTotalCost: number,
  challengeId?: string,
  challengeCost?: number,
  challengeName?: string,
  challengeSportIcon?: string,
  challengeSportName?: string,
  competitionIcon?: string,
  currentStep: number,
  currencyCode: string,
  disableAutoFill?: boolean,
  disableSelectLineup?: boolean,
  displayLoadLineup?: boolean,
  editMode?: boolean,
  enableUploadLineup?: boolean,
  errorMessages: Array<string>,
  lineupTypeFilters: Array<string>,
  isAnimating?: boolean,
  isEnteringChallenge: boolean,
  isLoadingTeams: boolean,
  isH2H: boolean,
  isRacing?: boolean,
  isRapidfire: boolean,
  isSignedIn: boolean,
  smartLineUpFailed?: boolean,
  lineUp?: Array<Object>,
  loadableLineups: Array<Object>,
  lockAll: () => void,
  insiderPlayers?: Object,
  maxEntries: number,
  numUnlocked: number,
  onAutoFill: () => void,
  onClear: () => void,
  onImport: (query: Object) => void,
  onLoadLineUp: (selectionHash: string) => void,
  pxc?: Object,
  salaryCap?: number,
  salaryExpenditure: number,
  selectedLineupIds: Array<number>,
  selectFilter: filter => void,
  setSelectedLineupIds: (lineupIds: Array<number>) => void,
  slateId: number,
  submissionComplete: boolean,
  submitMultipleEntries: (entryIds: Array<number>) => void,
  successfulEntryIds: Array<number>,
  toggleLineupFavorite: (id: number, favorite: boolean) => void,
  totalCost: number,
  totalSteps: number,
  unlockAll: () => void
};

type State = {
  addedToCart?: boolean,
  showImportModal: boolean,
  selectAll: boolean,
  willSubmit?: boolean
};

const LineUpsModal = styled(Modal)(({ theme }) => ({
  backgroundColor: theme.color.lightBackground || theme.color.white
}));

const LineupAllocationBox = styled.div(({ theme }) => ({
  backgroundColor: theme.color.basis2,
  [theme.mediaQuery.desktopAndUp]: {
    borderTopLeftRadius: theme.borderRadius.default,
    borderTopRightRadius: theme.borderRadius.default
  }
}));

const ContestButtonContainer = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  backgroundColor: theme.color.basis3
}));

const LineupAllocationTitleRow = styled.div(
  {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'row',
    textTransform: 'uppercase',
    width: '100%',
    borderRadius: '6px 6px 0 0',
    overflow: 'hidden',
    paddingTop: 5
  },
  ({ theme }) => ({
    fontSize: theme.fontSize.default,
    fontWeight: theme.fontWeight.demiBold,
    color: theme.color.white,
    [theme.mediaQuery.desktopAndUp]: {
      paddingTop: 0
    }
  })
);

const PlayerIcon = styled.img({
  marginRight: 5,
  minWidth: 16,
  width: 16,
  height: 16
});

const Projection = styled.span(({ theme }) => ({
  color: theme.color.basis1
}));

const BottomBar = styled.div(({ theme }) => ({
  backgroundColor: theme.color.basis5,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  ...padding(5, null)
}));

const PlayersSelected = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing.small,
  color: theme.color.basis1,
  fontSize: theme.fontSize.small
}));

const ButtonContainer = styled.div({
  display: 'flex',
  alignItems: 'center'
});

const FinePrint = styled.div(({ theme }) => ({
  fontSize: theme.fontSize.tiny,
  color: theme.color.basis4
}));

const LockAllButton = styled(RowButton)(({ theme }) => ({
  color: theme.color.basis1,
  ...hover({
    color: theme.color.primary
  })
}));

const RemoveButton = styled(RowButton)(({ theme }) => ({
  color: theme.color.basis1,
  ...hover({
    color: theme.color.danger
  })
}));

const StretchButton = styled(Button)({
  display: 'flex',
  flexBasis: '50%',
  justifyContent: 'space-between',
  alignItems: 'center',
  outline: 'none'
});

const SelectButton = styled.button(({ theme }) => ({
  display: 'flex',
  flexBasis: '50%',
  alignItems: 'center',
  fontSize: theme.fontSize.default,
  justifyContent: 'space-between',
  backgroundColor: theme.color.basis3,
  textTransform: 'uppercase',
  color: theme.color.white
}));

const ImportTeamContainer = styled.div(({ theme }) => ({
  backgroundColor: theme.color.basis6,
  padding: 1,
  margin: theme.spacing.tiny
}));

const SubTitle = styled.div(({ theme }) => ({
  flexGrow: 1,
  marginBottom: theme.spacing.xsmall
}));

const ModalHeader = styled(LineUpHeaderWrapper)(({ theme }) => ({
  fontSize: theme.fontSize.large,
  justifyContent: 'start',
  textTransform: 'uppercase',
  [theme.mediaQuery.mobileOnly]: {
    fontSize: theme.fontSize.default
  }
}));

const ModalSubHeader = styled(LineUpHeaderWrapper)(({ theme }) => ({
  fontSize: theme.fontSize.medium,
  justifyContent: 'space-between',
  flexDirection: 'column',
  alignItems: 'stretch',
  [theme.mediaQuery.mobileOnly]: {
    alignItems: 'flex-start',
    '& > div:not(:last-child)': {
      marginBottom: theme.spacing.small
    }
  }
}));

const Clickable = styled.div({
  display: 'flex',
  alignItems: 'center'
});

const FilterBar = styled.div(({ theme }) => ({
  display: 'flex',
  fontSize: theme.fontSize.medium,
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    flexDirection: 'column'
  }
}));

const ActionBar = styled.div(({ theme }) => ({
  display: 'flex',
  fontSize: theme.fontSize.medium,
  justifyContent: 'flex-end',
  '& > div:not(:last-child)': {
    marginRight: theme.spacing.default
  },
  textTransform: 'uppercase',
  marginTop: theme.spacing.tiny
}));

const StyledRadioButton = styled(RadioButton)(({ theme }) => ({
  color: theme.color.white
}));

const ModalFooter = styled(LineUpHeaderWrapper)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  fontSize: theme.fontSize.default,
  alignItems: 'center',
  padding: '0',
  justifyContent: 'flex-start',
  [theme.mediaQuery.tabletPortraitAndUp]: {
    justifyContent: 'flex-end',
    '& > div:not(:last-child)': {
      marginRight: theme.spacing.large
    }
  }
}));

const Cell = styled.div(({ theme }) => ({
  [theme.mediaQuery.mobileOnly]: {
    padding: '0 0 0.8em 0.8em'
  }
}));

const ImportButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  flexBasis: '200px',
  lineHeight: '1.2 !important',
  [theme.mediaQuery.mobileOnly]: {
    flexBasis: '100%'
  }
}));

const FooterName = styled.div(({ theme }) => ({
  [theme.mediaQuery.mobileOnly]: {
    width: '100%',
    padding: '0.8em 0.8em 0 0.8em'
  }
}));

const ButtonsWrapper = styled.div({
  display: 'flex',
  justifyContent: 'center'
});

const ModalButton = styled(Button)({
  minWidth: '6rem',
  margin: '0.5em',
  textAlign: 'center',
  minHeight: 51
});

const ImportNum = styled.span(({ danger, theme }) => ({
  color: danger ? theme.color.danger : 'inherit'
}));

const Label = styled.div(({ theme }) => ({
  margin: theme.spacing.large
}));

class LineUpAllocation extends React.Component<Props> {
  props: Props;

  state: State = {
    addedToCart: false,
    showImportModal: false,
    selectAll: false,
    selectNotEntered: false,
    willSubmit: false
  };

  static defaultProps = {
    // balance: undefined,
    enableSmartLineUp: false,
    bonusBalance: undefined,
    smartLineUpFailed: false,
    challengeId: undefined,
    challengeCost: undefined,
    challengeName: undefined,
    challengeSportIcon: undefined,
    challengeSportName: undefined,
    competitionIcon: undefined,
    disableAutoFill: false,
    disableSelectLineup: false,
    displayLoadLineup: false,
    editMode: false,
    enableUploadLineup: false,
    lineUp: [],
    insiderPlayers: undefined,
    isAnimating: false,
    isRacing: false,
    pxc: undefined,
    salaryCap: undefined
  };

  toggleImportModal = () => {
    const { showImportModal } = this.state;
    this.setState({ showImportModal: !showImportModal });
  };

  handleImport = () => {
    const { challengeId, isSignedIn, slateId, onImport } = this.props;

    if (isSignedIn) {
      onImport({ slate_id: slateId, challenge_id: challengeId });
      this.toggleImportModal();
    }
  };

  toggleSelectAll = () => {
    const { loadableLineups, setSelectedLineupIds } = this.props;
    const { selectAll } = this.state;
    const ids = selectAll ? [] : loadableLineups.map(({ id }) => id);

    this.setState({ selectAll: !selectAll });
    setSelectedLineupIds(ids);
  };

  toggleSelectLineup = (id: number) => {
    const { selectedLineupIds, setSelectedLineupIds } = this.props;

    if (selectedLineupIds.includes(id)) {
      this.setState({ selectAll: false });
      setSelectedLineupIds(without([id], selectedLineupIds));
    } else {
      setSelectedLineupIds([...selectedLineupIds, id]);
    }
  };

  getProduct = () => {
    const {
      challengeName: name,
      challengeId: id,
      isRapidfire,
      isH2H,
      challengeCost: price,
      challengeSportName: sportName,
      selectedLineupIds
    } = this.props;

    let brand = price === 0 ? 'Free' : 'Paid';

    if (isRapidfire) {
      brand = 'Rapidfire';
    } else if (isH2H) {
      brand = 'Head to head';
    }

    return {
      name,
      id,
      brand,
      category: sportName,
      price,
      quantity: selectedLineupIds.length
    };
  };

  previewTransfer = ({ bonus = 0 }) => {
    const { challengeId, challengeName, totalCost, pxc } = this.props;

    pxc.previewTransfer({
      transactions: [
        {
          amount: totalCost - bonus,
          details: `${challengeName} (Entry Fee)`,
          metadata: {
            challenge_id: challengeId,
            lineups: true
          }
        }
      ]
    });
  };

  handleCancel = () => {
    const { currencyCode } = this.props;
    const { addedToCart } = this.state;

    if (addedToCart && window && window.dataLayer) {
      const product = this.getProduct();

      if (product) {
        window.dataLayer.push({
          event: 'removeFromCart',
          ecommerce: {
            currencyCode,
            remove: {
              products: [product]
            }
          }
        });
      }
    }

    this.setState({
      addedToCart: false,
      willSubmit: false
    });
  };

  handleSubmit = () => {
    const {
      currencyCode,
      setSelectedLineupIds,
      selectedLineupIds,
      maxEntries,
      lineups
    } = this.props;

    if (window && window.dataLayer) {
      const product = this.getProduct();

      if (product) {
        window.dataLayer.push({
          event: 'addToCart',
          ecommerce: {
            currencyCode,
            add: {
              products: [product]
            }
          }
        });

        window.dataLayer.push({
          event: 'checkout',
          ecommerce: {
            currencyCode,
            checkout: {
              actionField: {
                step: 1
              },
              products: [product]
            }
          }
        });
      }
    }

    const selectedLineups = lineups.filter(({ id }) => selectedLineupIds.includes(id));
    const trimmedLineups = selectedLineups.slice(0, maxEntries).map(({ id }) => id);

    setSelectedLineupIds(trimmedLineups);

    this.setState({
      addedToCart: true,
      willSubmit: true
    });
  };

  handleConfirm = () => {
    const {
      // balance,
      bonusBalance,
      currencyCode,
      pxc,
      submitMultipleEntries,
      totalCost
    } = this.props;

    const balance =
      (window && window.portal && window.portal.user && window.portal.user.account_balance()) || 0;

    if (window && window.dataLayer) {
      const product = this.getProduct();

      if (product) {
        window.dataLayer.push({
          event: 'checkout',
          ecommerce: {
            currencyCode,
            checkout: {
              actionField: {
                step: 2
              },
              products: [product]
            }
          }
        });
      }

      this.setState({ addedToCart: false });
    }

    const { bonus } = getCostSplit(totalCost, balance, bonusBalance);

    if (!pxc.isEnabled || totalCost - bonus <= 0) {
      submitMultipleEntries();
      return;
    }

    this.previewTransfer({ bonus });
  };

  canImport = () => {
    const { maxEntries, selectedLineupIds } = this.props;
    return selectedLineupIds.length > 0;
  };

  clearSelection = () => {
    const { setSelectedLineupIds, selectedLineupIds } = this.props;

    if (selectedLineupIds.length > 0) setSelectedLineupIds([]);
  };

  render() {
    const {
      allLocked,
      challengeName,
      challengeSportIcon,
      competitionIcon,
      currentStep,
      disableAutoFill,
      disableSelectLineup,
      displayLoadLineup,
      editMode,
      enableUploadLineup,
      errorMessages,
      lineupTypeFilters,
      isAnimating,
      enableSmartLineUp,
      isEnteringChallenge,
      smartLineUpFailed,
      isLoadingTeams,
      isRacing,
      isSignedIn,
      loadableLineups,
      lockAll,
      lineUp,
      lineups,
      maxEntries,
      numUnlocked,
      onAutoFill,
      onClear,
      onLoadLineUp,
      pxc,
      salaryCap,
      insiderPlayers,
      salaryExpenditure,
      selectedLineupIds,
      submissionComplete,
      successfulEntryIds,
      selectFilter,
      toggleLineupFavorite,
      totalCost,
      totalSteps,
      unlockAll
    } = this.props;

    const projections = round(
      sum(
        (lineUp || []).map(
          ({ idStr, scoreProjected }) =>
            get(`${idStr}.insiderScore`, insiderPlayers) || scoreProjected
        )
      ),
      2
    );

    const { showImportModal, selectAll, willSubmit } = this.state;
    const canSelectLineup = !disableSelectLineup && !editMode && enableUploadLineup;
    const playerIcon = competitionIcon || (isRacing ? horseSvg : playerSvg);

    const favoriteCount = lineups.filter(lineup => lineup.favorite).length;
    const unsubmittedCount = lineups.filter(lineup => lineup.entryCount === 0).length;

    return (
      <div>
        <LineupAllocationBox>
          <LineupAllocationTitleRow>
            {displayLoadLineup && (
              <StretchButton
                autoBorderRadius={false}
                size="small"
                onClick={this.handleImport}
                kind="secondary"
                disabled={!isSignedIn}
                style={{
                  display: 'flex'
                }}
              >
                Import
                <Icon type="icon-import" />
              </StretchButton>
            )}
            {isRacing && isSignedIn && (
              <StretchButton
                autoBorderRadius={false}
                size="small"
                link="/combo-calculator"
                kind="info"
              >
                Combo
                <FontAwesomeIcon icon={faCalculator} />
              </StretchButton>
            )}
            <AutofillButton
              currentStep={currentStep}
              disabled={disableAutoFill}
              isAnimating={isAnimating}
              numUnlocked={numUnlocked}
              onAutoFill={onAutoFill}
              label={enableSmartLineUp && !smartLineUpFailed ? 'Smart LineUp' : 'AutoFill'}
              onClear={onClear}
            />
          </LineupAllocationTitleRow>
          <Responsive mobile>
            <SalaryUtilisation salaryCap={salaryCap} salaryExpenditure={salaryExpenditure} />
          </Responsive>
          <BottomBar>
            <PlayersSelected>
              <PlayerIcon alt="player" src={playerIcon} />
              <span style={{ paddingTop: 3 }}>{`${currentStep}/${totalSteps}`}</span>
            </PlayersSelected>
            {enableSmartLineUp && <Projection>PROJECTION: {projections}</Projection>}
            <ButtonContainer>
              <LockAllButton
                type={allLocked ? 'unlock' : 'lock'}
                onSelect={allLocked ? unlockAll : lockAll}
              />
              <RemoveButton type="remove" onSelect={onClear} />
            </ButtonContainer>
          </BottomBar>
        </LineupAllocationBox>
        {showImportModal && (
          <LineUpsModal bare isOpen lightCloseButton size="large" onCancel={this.toggleImportModal}>
            {isLoadingTeams && <Loader pad />}
            {!isLoadingTeams && (
              <div>
                <ModalHeader dark>Import Lineup</ModalHeader>
                <ModalSubHeader>
                  <FilterBar>
                    <SubTitle>
                      Select team(s) you would like to import and submit (
                      <ImportNum danger={selectedLineupIds.length > maxEntries}>
                        {selectedLineupIds.length}
                      </ImportNum>{' '}
                      / <ImportNum>{maxEntries}</ImportNum>){' '}
                      <Tappable onTap={this.clearSelection}>
                        <Icon type="icon-cross-circle" size={12} />
                      </Tappable>
                    </SubTitle>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Tappable
                        component={Clickable}
                        onTap={() => selectFilter(FAVORITE)}
                        style={{ marginRight: '8px' }}
                      >
                        <StyledRadioButton checked={lineupTypeFilters.includes(FAVORITE)} />
                        <span style={{ marginLeft: '5px', textTransform: 'uppercase' }}>
                          Show favourite teams ({favoriteCount})
                        </span>
                      </Tappable>
                      <Tappable component={Clickable} onTap={() => selectFilter(UNSUBMITTED)}>
                        <StyledRadioButton checked={lineupTypeFilters.includes(UNSUBMITTED)} />
                        <span style={{ marginLeft: '5px', textTransform: 'uppercase' }}>
                          Show Teams not entered ({unsubmittedCount})
                        </span>
                      </Tappable>
                    </div>
                  </FilterBar>
                  <ActionBar>
                    {canSelectLineup && (
                      <Tappable component={Clickable} onTap={this.toggleSelectAll}>
                        <Button kind={selectAll ? 'warning' : 'success'}>
                          {selectAll ? 'Deselect' : 'Select'} All
                          {lineupTypeFilters.includes(UNSUBMITTED)
                            ? ' Unsubmitted '
                            : lineupTypeFilters.includes(FAVORITE)
                            ? ' Favourites '
                            : ' '}
                          ({loadableLineups.length})
                        </Button>
                      </Tappable>
                    )}
                  </ActionBar>
                </ModalSubHeader>
                {loadableLineups.length === 0 ? (
                  <Label>No teams available to import</Label>
                ) : (
                  <Rows mobile={1} desktop={3}>
                    {loadableLineups.map(
                      ({
                        id,
                        favorite,
                        name,
                        entryCount,
                        selection,
                        selectionHash,
                        sportIcon,
                        statusId,
                        statusLabel
                      }) => (
                        <ImportTeamContainer key={id}>
                          <LineUpHeader
                            id={id}
                            entriesLabel={`Entries in ${t('challenge')}`}
                            favorite={favorite}
                            isLive={false}
                            name={name}
                            numEntries={!editMode ? entryCount : undefined}
                            sportIcon={sportIcon}
                            statusId={statusId}
                            statusLabel={statusLabel}
                            onToggleFavorite={toggleLineupFavorite}
                          />
                          <LineUpTable
                            positions={selection}
                            renderPosition={(pos, i) => (
                              <LineUpSection
                                key={`${pos.id}_${i}`}
                                filled={pos.filled}
                                hasError={pos.hasError}
                                id={pos.id}
                                name={pos.name}
                                selections={pos.selections}
                                abbr={pos.abbr}
                                renderEntity={entity => (
                                  <SelectedEntityRow
                                    key={entity.id}
                                    awayTeam={entity.awayAbbr}
                                    bonus={entity.bonus}
                                    disabled={entity.disabled}
                                    gameOrder={entity.gameOrder}
                                    showButtons={false}
                                    homeTeam={entity.homeAbbr}
                                    id={entity.id}
                                    idStr={entity.idStr}
                                    image={entity.imageURI}
                                    initials={entity.initials}
                                    isHome={entity.isHome}
                                    isRacing={isRacing}
                                    isScratched={entity.isScratched}
                                    labels={entity.labels}
                                    locked={entity.locked}
                                    name={entity.name}
                                    place={entity.placeProjected}
                                    positionId={entity.positionId}
                                    positionLabel={entity.positionAbbr}
                                    reverseOrder={entity.reverseOrder}
                                    rider={entity.rider}
                                    riderImage={entity.riderImage}
                                    salary={getFactoredEntityPositionSalary(
                                      entity.salary,
                                      entity.positions,
                                      pos.key
                                    )}
                                    trainer={entity.trainer}
                                    win={entity.winProjected}
                                  />
                                )}
                              />
                            )}
                          />
                          <ContestButtonContainer>
                            <StretchButton
                              autoBorderRadius={false}
                              kind="secondary"
                              onClick={() => {
                                onLoadLineUp(selectionHash);
                                this.toggleImportModal();
                              }}
                            >
                              Import
                              <Icon type="icon-import" />
                            </StretchButton>
                            {canSelectLineup && (
                              <SelectButton
                                autoBorderRadius={false}
                                kind="reset"
                                onClick={() => this.toggleSelectLineup(id)}
                              >
                                Select
                                <RadioTick selected={selectedLineupIds.includes(id)} />
                              </SelectButton>
                            )}
                          </ContestButtonContainer>
                        </ImportTeamContainer>
                      )
                    )}
                  </Rows>
                )}
                {canSelectLineup && loadableLineups.length > 0 && (
                  <ModalFooter>
                    <FooterName>
                      <SportsIcon type={`icon-sport-${challengeSportIcon}`} />
                      <span>{challengeName}</span>
                    </FooterName>
                    <Cell>
                      <span>Entry Cost: </span>
                      <span>
                        <CurrencyIcon />
                        {totalCost}
                      </span>
                    </Cell>
                    <Cell>
                      Import:{' '}
                      <ImportNum danger={selectedLineupIds.length > maxEntries}>
                        {selectedLineupIds.length}
                      </ImportNum>{' '}
                      / <ImportNum>{maxEntries}</ImportNum>
                    </Cell>
                    <Cell>
                      <Tappable onTap={this.clearSelection}>
                        <Icon type="icon-cross-circle" />
                      </Tappable>
                    </Cell>
                    <ImportButton
                      autoBorderRadius={false}
                      kind={selectedLineupIds.length > maxEntries ? 'warning' : 'success'}
                      size="medium"
                      disabled={!this.canImport()}
                      onClick={this.handleSubmit}
                    >
                      Submit
                      {selectedLineupIds.length > maxEntries &&
                        ` first ${maxEntries} of ${selectedLineupIds.length} teams`}
                    </ImportButton>
                  </ModalFooter>
                )}
              </div>
            )}
          </LineUpsModal>
        )}
        {canSelectLineup && willSubmit && !submissionComplete && (
          <OpenChallengeModal
            cost={totalCost}
            bonusFreeCost={0}
            showSubmitSuccess={submissionComplete}
            isSubmitting={isEnteringChallenge}
            onCancel={this.handleCancel}
            multiEntry={selectedLineupIds.length > 1}
            onSubmit={this.handleConfirm}
            pxc={pxc}
            showEntryConfirmation
          />
        )}
        {canSelectLineup && submissionComplete && (
          <Modal isOpen size="medium" showCloseButton={false}>
            <Title noWhiteSpace>{successfulEntryIds.length > 0 ? 'Success' : 'Fail'}</Title>
            {successfulEntryIds.length > 0 && (
              <div style={{ textAlign: 'center', padding: '1em' }}>
                You have successfully submitted {successfulEntryIds.length}{' '}
                {pluralize('entry', successfulEntryIds.length)}!
                <FinePrint>
                  Your tickets: {successfulEntryIds.map(id => `#${id}`).join(', ')}
                </FinePrint>
              </div>
            )}
            {selectedLineupIds.length - successfulEntryIds.length > 0 && (
              <FormValidationMessage
                message={[
                  {
                    message: `Unfortunately ${selectedLineupIds.length -
                      successfulEntryIds.length} ${pluralize(
                      'entry',
                      selectedLineupIds.length - successfulEntryIds.length
                    )} failed.`
                  },
                  ...errorMessages.map(str => ({
                    message: str
                  }))
                ]}
              />
            )}
            <ButtonsWrapper>
              <ModalButton kind="secondary" size="medium" link="/lobby">
                Lobby
              </ModalButton>
              <ModalButton kind="primary" size="medium" link="/my-challenges">
                {t('My Challenges')}
              </ModalButton>
              <ModalButton kind="primary" size="medium" link="/my-teams">
                {t('My Teams')}
              </ModalButton>
            </ButtonsWrapper>
          </Modal>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  const loadableLineups = loadableLineupsSelector(state);
  const selectedLineupIds = selectedLineupIdsSelector(state);
  const challengeCost = get('challenge.cost', state) || 0;
  const entryCap = get('challenge.entryCap', state) || 0;
  const maxEntriesPerPerson = get('challenge.maxEntriesPerPerson', state);
  const userEntryCount = get('challenge.userEntryCount', state) || 0;

  let maxEntries = loadableLineups.length;

  if (maxEntriesPerPerson > 0 && maxEntriesPerPerson - userEntryCount < maxEntries) {
    maxEntries = maxEntriesPerPerson - userEntryCount;
  } else if (entryCap > 0 && entryCap - userEntryCount < maxEntries) {
    maxEntries = entryCap - userEntryCount;
  }

  const lineUp = lineUpSelector(state);

  return {
    allLocked: every({ locked: true }, lineUp),
    // balance: get('user.currentUser.balance', state),
    bonusBalance: get('user.currentUser.bonusPack.totalBalance', state),
    challengeCost,
    challengeId: get('challenge.id', state),
    challengeSportIcon: get('challenge.sportIcon', state),
    challengeName: get('challenge.name', state),
    challengeSportName: get('challenge.sportName', state),
    competitionIcon: get('challenge.competitionIcon', state),
    currencyCode: get('settings.currency.code', state),
    didAutofill: get('lineUp.didAutofill', state),
    displayLoadLineup: state.settings.displayLoadLineup,
    enableUploadLineup: !!get('settings.enableUploadLineup', state),
    lineUp,
    lineups: get('myTeams.lineups', state),
    errorMessages: get('myTeams.errorMessages', state),
    isEnteringChallenge: !!get('myTeams.isEnteringChallenge', state),
    isH2H: get('challenge.isH2H', state),
    isLoadingTeams: state.myTeams.isSlateLineupsLoading,
    isRacing: get('challenge.isRacing', state),
    isRapidfire: get('challenge.isRapidfire', state),
    isSignedIn: state.user.isSignedIn,
    lineupTypeFilters: get('myTeams.lineupTypeFilters', state) || [],
    insiderPlayers: get('fantasyInsider.players', state),
    loadableLineups,
    maxEntries,
    onLoadLineUp: challengeActions.loadLineUp,
    smartLineUpFailed: get('lineUp.smartLineUpFailed', state),
    selectedLineupIds,
    slateId: get('challenge.slateId', state),
    submissionComplete: get('myTeams.submissionComplete', state),
    successfulEntryIds: get('myTeams.successfulEntryIds', state),
    totalCost: selectedLineupIds.length * challengeCost
  };
}

function mapDispatchToProps(dispatch, { pxc }) {
  return {
    lockAll() {
      dispatch(challengeActions.lockAll());
    },
    unlockAll() {
      dispatch(challengeActions.unlockAll());
    },
    onImport(query) {
      dispatch(myTeamsActions.getSlateLineUps(query));
    },
    onLoadLineUp(id) {
      dispatch(challengeActions.loadLineUp(id));
    },
    selectFilter(filter) {
      dispatch(myTeamsActions.switchLineupTypeFilter(filter));
    },
    toggleLineupFavorite(id, favorite) {
      dispatch(myTeamsActions.toggleLineupFavorite(id, favorite));
    },
    setSelectedLineupIds(lineupIds) {
      dispatch(myTeamsActions.setSelectedLineupIds(lineupIds));
    },
    submitMultipleEntries() {
      if (pxc && pxc.isPxcEnabled) {
        dispatch(myTeamsActions.submitMultiplePxcEntries());
      } else {
        dispatch(myTeamsActions.submitMultipleEntries());
      }
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LineUpAllocation);
